import type {AllowedUrlParams, RegisterBody} from "~/api/v1/auth/AuthRepository";
import type {CompanyPayload, ListCompaniesQueryParams} from "~/api/v1/companies/CompanyRepository";

interface RegisterPayload {
    body: RegisterBody;
    params?: AllowedUrlParams;
}

export interface CompanyStore {
    company_id: string;
    country?: string;
    public_name?: string;
    ape_code?: string,
    address?: string;
    city?: string;
    postal_code?: string;
    siren?: string;
    siret?: string;
    intra_community_vat?: string;
    phone?: string;
    business_name?: string;
    legal_form_id?: string;
    subject_vat: boolean;
    created_at: string;
    updated_at: string;
    contact: {
        first_name?: string;
        last_name?: string;
        email?: string;
    };
    company_size_id?: string;
    industry_id?: string;
}

export const useCompanyStore = defineStore('company', {
    state: () => ({
        company: {} as CompanyStore,
        companies: [] as CompanyStore[],
        totalCompanies: 0,
        isLoading: false,
        error: null as string | null,
    }),
    
    getters: {
        getCompanies: (state) => state.companies,
        getTotalCompanies: (state) => state.totalCompanies,
    },
    
    actions: {
        
        // API methods
        async patch(payload: Partial<CompanyStore>, config: { skipGlobalErrorAlert?: boolean } = {}) {
            const { $api } = useNuxtApp();
            const flatPayload = { ...payload };
            if (flatPayload.contact) {
                flatPayload.contact_first_name = flatPayload.contact.first_name;
                flatPayload.contact_last_name = flatPayload.contact.last_name;
                flatPayload.contact_email = flatPayload.contact.email;
                delete flatPayload.contact;
            }
            // API expects company_legal_form_id, not legal_form_id
            if (flatPayload.legal_form_id) {
                flatPayload.company_legal_form_id = flatPayload.legal_form_id;
                delete flatPayload.legal_form_id;
            }
            await $api().v1.company.patchCompany(flatPayload, config);
        },
        
        async getStripeAccountUrl(companyId: string, returnUrl?: string) {
            try {
                this.isLoading = true;
                this.error = null;
                
                const { $api } = useNuxtApp();
                const response = await $api().v1.company.getStripeAccountUrl(companyId, returnUrl);
                return response.account_link_url;
            } catch (error) {
                console.error('Error getting Stripe account URL:', error);
                this.error = error instanceof Error ? error.message : 'Failed to get Stripe account URL';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
        
        async getStripeAccountStatus() {
            try {
                this.isLoading = true;
                this.error = null;
                const companyId = this.companies[0].company_id;
                
                const { $api } = useNuxtApp();
                const response = await $api().v1.company.getStripeAccountStatus(companyId);
                return response.is_enabled;
            } catch (error) {
                console.error('Error getting Stripe account status:', error);
                this.error = error instanceof Error ? error.message : 'Failed to get Stripe account status';
                // Return false on error to assume account is not enabled
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        
        async getLegalForms() {
            const { $api } = useNuxtApp();
            return $api().v1.company.getLegalForms();
        },
        
        async searchCompanies(query: string, source: 'external' | 'internal' = 'internal', params: any = {}) {
            try {
                this.isLoading = true;
                this.error = null;
                
                const { $api } = useNuxtApp();
                return await $api().v1.company.searchCompanies(query, source, params);
            } catch (error) {
                console.error('Error searching companies:', error);
                this.error = error instanceof Error ? error.message : 'Failed to search companies';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
        
        async createCompany(payload: CompanyPayload, config: { skipGlobalErrorAlert?: boolean } = {}) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();


                const finalPayload = {
                    business_name: payload.business_name,
                    public_name: payload.public_name || payload.business_name,
                    phone: payload.phone,
                    address: payload.address,
                    siren: payload.siret.substring(0, 9),
                    siret: payload.siret,
                    ape_code: payload.ape_code,
                    intra_community_vat: payload.intra_community_vat,
                    subject_vat: payload.subject_vat, // Pass subject_vat directly
                    legal_form_id: payload.legal_form_id,
                    // Note: contact is required and not optional
                    contact: payload.contact || {
                        first_name: '',
                        last_name: '',
                        email: ''
                    }
                };

                const newCompany = await $api().v1.company.createCompany(finalPayload, config);

                // Update state with newly created company
                this.company = newCompany;
                
                // Create Stripe account for the new company (no response needed)
                try {
                    await $api().v1.company.createStripeAccount(newCompany.company_id);
                } catch (stripeError) {
                    console.error('Error creating Stripe account for company:', stripeError);
                    // We continue even if creating the Stripe account fails
                }
                
                // Create Stripe customer for the new company (no response needed)
                try {
                    await $api().v1.company.createStripeCustomer(newCompany.company_id);
                } catch (stripeError) {
                    console.error('Error creating Stripe customer for company:', stripeError);
                    // We continue even if creating the Stripe customer fails
                }
                
                return newCompany;
            } catch (error) {
                console.error('Error creating company:', error);
                this.error = error instanceof Error ? error.message : 'Failed to create company';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
        
        async fetchCompanies(params: ListCompaniesQueryParams = {}) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                // Always fetch only the current user's companies by default
                const response = await $api().v1.company.fetchAllCompanies({ me: true, ...params });
                
                this.companies = response.companies;
                this.totalCompanies = response.total;
                
                return response;
            } catch (error) {
                console.error('Error fetching companies:', error);
                this.error = error instanceof Error ? error.message : 'Failed to fetch companies';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async fetchCompany(companyId: string) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                const response = await $api().v1.company.fetchCompany(companyId);

                this.company = response;

                return response;
            } catch (error) {
                console.error('Error fetching company:', error);
                this.error = error instanceof Error ? error.message : 'Failed to fetch company';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async uploadLogo(companyId: string, file: File): Promise<string | null> {
            const { $api } = useNuxtApp();
            const formData = new FormData();
            formData.append('file', file);
            const response = await $api().client(`/v2/companies/${companyId}/logo`, {
                method: 'POST',
                body: formData,
            }) as { signed_logo_url: string | null };
            return response.signed_logo_url;
        },

        async fetchLogoUrl(companyId: string): Promise<string | null> {
            const { $api } = useNuxtApp();
            const response = await $api().client(`/v2/companies/${companyId}/logo`, {
                method: 'GET',
            }) as { signed_logo_url: string | null };
            return response.signed_logo_url;
        },

        async updateCompany(companyId: string, payload: { company_size_id?: string; industry_id?: string }) {
            try {
                this.isLoading = true;
                this.error = null;

                const { $api } = useNuxtApp();
                await $api().v1.company.patchCompany({
                    company_id: companyId,
                    ...payload,
                });

                // Update local state so subsequent reads (e.g. mission wizard prefill) see the new values
                if (this.company?.company_id === companyId) {
                    if (payload.company_size_id !== undefined) {
                        this.company.company_size_id = payload.company_size_id;
                    }
                    if (payload.industry_id !== undefined) {
                        this.company.industry_id = payload.industry_id;
                    }
                }
            } catch (error) {
                console.error('Error updating company:', error);
                this.error = error instanceof Error ? error.message : 'Failed to update company';
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
    },
});